import { bookingIdToDiseaseId } from 'constants/diseases'

import { useState } from 'react'
import { get, findIndex, find } from 'lodash'
import T from 'prop-types'
import Joi from 'joi'
import { useHistory } from 'react-router-dom'
import { createVaccination, patchVaccination } from 'lib/requests'
import useSession from 'hooks/useSession'
import Button from 'components/Button'
import Message from 'components/Message'
import getGenderText from 'apps/booking/helpers/getGenderText'

import Step from '../../components/Step'

export const validator = () =>
  Joi.object({
    confirm: Joi.boolean().invalid(false),
  }).unknown(true)

export const controlQuestionPredicate = (values) => {
  // Skip this question when booking "on behalf of" (since the API is currently unable to handle it)
  if (values?.details?.on_behalf_of?.national_id) {
    return false
  }
  return true
}

export default function ControlQuestion({ changeStep, values, onChange, orgUnit, bookingType }) {
  const history = useHistory()
  const {
    session: { user_id, first_name, last_name },
  } = useSession()
  const [error, setError] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const question = 'WANTS_VACCINE'
  const self_declarations = get(values, 'vaccination.self_declaration', [])

  const handleChange = (answer) => () => {
    const value = { question, answer }
    const index = findIndex(self_declarations, { question })

    onChange(
      'vaccination.self_declaration',
      index === -1
        ? [...self_declarations, value]
        : [...self_declarations.slice(0, index), value, ...self_declarations.slice(index + 1)],
    )
  }

  const value = find(self_declarations, { question })
  const title = getGenderText('Bekreft at %s ønsker vaksine', values)
  const confirmed = get(value, 'answer', undefined)

  const isBookingOnBehalfOf = Boolean(values?.details?.on_behalf_of?.national_id)

  async function handleChangeStep() {
    if (confirmed) {
      return changeStep()
    }

    if (values?.vaccinationId) {
      setIsSubmitting(true)
      const response = await patchVaccination(
        values?.vaccinationId,
        {
          vaccination_status: 'CANCELLED',
          self_declaration: values?.vaccination?.self_declaration,
        },
        false,
        orgUnit.id,
      )
      setIsSubmitting(false)
      if (response.error) {
        return setError(get(response, 'error.message', 'Noe gikk feil'))
      }
    } else {
      setIsSubmitting(true)

      const diseaseId = bookingIdToDiseaseId(bookingType?.id)

      // Prepare our data
      const postData = {
        person: {
          national_id: user_id,
          first_name,
          last_name,
        },
        vaccination: {
          ...(values?.vaccination || {}),
          org_unit: orgUnit.id,
          disease_id: diseaseId,
          vaccination_status: 'NOT_COMPLETED',
        },
      }

      const response = await createVaccination({ data: postData, ouId: orgUnit.id })
      setIsSubmitting(false)
      if (response.error) {
        return setError(get(response, 'error.message', 'Noe gikk feil'))
      }
    }

    history.push(`/${orgUnit.slug}/vil-ikke`)
  }

  return (
    <Step
      title={title}
      onSubmit={handleChangeStep}
      submit="Gå videre"
      submitDisabled={confirmed === undefined || isSubmitting}
      isLoading={isSubmitting}
      info={getGenderText(
        `
        For å holde oversikt over fremgangen i vaksinasjonsprogrammet, er det svært nyttig å vite
        om %s ikke ønsker en vaksine mot covid-19. Det kan du si ifra om her. Det vil da bli registrert,
        og %s vil ikke bli kontaktet aktivt igjen angående vaksine. Det er selvsagt mulig å ombestemme seg,
        men %s må da selv ta kontakt for å si fra.
      `,
        values,
      )}
    >
      <Button inline small onClick={handleChange(true)} selected={!!confirmed}>
        {isBookingOnBehalfOf
          ? getGenderText('Ja, %s ønsker vaksine mot koronavirus', values)
          : 'Ja, jeg ønsker vaksine mot koronavirus'}
      </Button>
      <Button inline small onClick={handleChange(false)} selected={confirmed === false}>
        {isBookingOnBehalfOf
          ? getGenderText('Nei, %s ønsker ikke vaksine', values)
          : 'Nei, jeg ønsker ikke vaksine'}
      </Button>

      {/* Show info when someone with an active vaccination says no */}
      {confirmed === false && values?.vaccinationId && (
        <p>
          <strong>
            Når du svarer 'Nei' vil det ikke automatisk avbestille timen din. Vi oppfordrer deg til
            å kontakte din lokale vaksinetjeneste og gi beskjed om at du ikke ønsker timen.
          </strong>
        </p>
      )}

      {error && (
        <Message type="warning" focus>
          {error}
        </Message>
      )}
    </Step>
  )
}

ControlQuestion.propTypes = {
  changeStep: T.func.isRequired,
  values: T.object.isRequired,
  onChange: T.func.isRequired,
  orgUnit: T.shape({
    id: T.string.isRequired,
    slug: T.string.isRequired,
  }).isRequired,
  bookingType: T.shape({
    id: T.string.isRequired,
    slug: T.string.isRequired,
    title: T.string.isRequired,
  }).isRequired,
}
